import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "../App";
import { Mail, Lock, LogIn, Chrome, Loader2 } from "lucide-react";
import NeuralNetworkBackground, { ScaleWatermark } from "../components/NeuralNetworkBackground";

const LOGO = "https://customer-assets.emergentagent.com/job_ai-legal-test/artifacts/trhkf0y7_justice%20ai%20logo.png";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [isLoading, setIsLoading] = useState(false);

  const from = location.state?.from?.pathname || "/dashboard";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, formData, { withCredentials: true });
      login(response.data.user, response.data.token);
      toast.success("Welcome back!");
      navigate(from, { replace: true });
    } catch (error) {
      const message = error.response?.data?.detail || "Login failed. Please check your credentials.";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <main className="flex-1 relative min-h-[calc(100vh-5rem)] bg-ink flex items-center justify-center py-16 px-4 overflow-hidden" data-testid="login-page">
      <div className="absolute inset-0 opacity-50"><NeuralNetworkBackground density={20} /></div>
      <ScaleWatermark />
      <div className="turquoise-aura -top-32 -left-40 opacity-50" />
      <div className="gold-aura -bottom-40 right-0 opacity-40" />
      <div className="grain-overlay" />

      <div className="relative z-10 w-full max-w-md glass-strong rounded-3xl p-8 sm:p-10" data-testid="login-card">
        <div className="text-center mb-8">
          <img src={LOGO} alt="AccessJustice" className="h-20 w-auto mx-auto mb-5" />
          <div className="eyebrow mb-2">Welcome Back</div>
          <h1 className="font-playfair text-3xl text-white">
            Sign in to <span className="italic text-aj-gradient">Justice-AI</span>
          </h1>
          <p className="font-inter text-sm text-white/55 mt-2">Access your account and continue your legal journey.</p>
        </div>

        <button
          type="button"
          onClick={handleGoogleLogin}
          data-testid="google-login-btn"
          className="w-full flex items-center justify-center gap-3 px-5 py-3 rounded-full border border-white/15 hover:border-turquoise/40 bg-white/[0.02] hover:bg-white/[0.05] text-white font-montserrat text-xs uppercase tracking-[0.16em] font-semibold transition-all"
        >
          <Chrome className="w-4 h-4" /> Continue with Google
        </button>

        <div className="relative my-7">
          <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-white/10" /></div>
          <div className="relative flex justify-center"><span className="bg-ink px-3 font-montserrat text-[10px] uppercase tracking-[0.22em] text-white/40">or with email</span></div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block font-montserrat text-[10px] uppercase tracking-[0.22em] text-white/55 mb-2">Email</label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35" />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="you@example.com"
                required
                data-testid="email-input"
                className="w-full bg-white/[0.04] border border-white/10 rounded-full pl-11 pr-4 py-3 font-inter text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-turquoise/60 focus:bg-white/[0.06] transition-all"
              />
            </div>
          </div>
          <div>
            <label className="block font-montserrat text-[10px] uppercase tracking-[0.22em] text-white/55 mb-2">Password</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35" />
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                required
                data-testid="password-input"
                className="w-full bg-white/[0.04] border border-white/10 rounded-full pl-11 pr-4 py-3 font-inter text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-turquoise/60 focus:bg-white/[0.06] transition-all"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={isLoading}
            data-testid="login-submit-btn"
            className="btn-aj-primary w-full !py-3.5 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isLoading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Signing In…</>) : (<><LogIn className="w-4 h-4" /> Sign In</>)}
          </button>
        </form>

        <p className="text-center font-inter text-sm text-white/55 mt-7">
          Don&apos;t have an account?{" "}
          <Link to="/register" data-testid="register-link" className="text-turquoise hover:text-turquoise-200 font-semibold transition-colors">
            Create one
          </Link>
        </p>
      </div>
    </main>
  );
};

export default Login;
